import React, { Component, CSSProperties, RefObject } from "react";
import { TextAlign, TextVerticalAlign } from "../shape-entities/types/textAlign";
import { DEFAULT_FONT_WEIGHT, LETTER_CASE_FIRST_LETTER_TO_LOWER, LETTER_CASE_FIRST_LETTER_TO_UPPER, LETTER_CASE_TO_LOWER, LETTER_CASE_TO_UPPER, LETTER_CASE_WORDS_FIRST_TO_UPPER, manualBreakLine, OVERFLOW_FONTSIZE, OVERFLOW_KEEP_AS_IS, OVERFLOW_LAYERSIZE, TEXT_DECORATION_LINETHROUGH, TEXT_DECORATION_NONE, TEXT_DECORATION_OVERLINE, TEXT_HORIZONTAL_ALIGN_LEFT, TEXT_HORIZONTAL_ALIGN_RIGHT, TEXT_ORIENTATION_VERTICAL } from "../shape-entities/render-text";
import { LineType } from "shape-entities/types/TextInsideShape";
import Shape, { AllowedShapeTypes, NO_TEXT_CURSOR_TYPES, RECTANGLE_EACH_LINE_TYPE, TEXT_INSIDE_SHAPE_TYPE, TEXT_WITH_ELEMENTS_TYPE } from "shape-entities/types/shape";
import { GradientFillUrl } from "shape-edit/forms/fields";
import { DEFAULT_LINE_CAP, DEFAULT_LINE_JOIN, LINE_CAP_CONSTS, LINE_JOIN_CONSTS, StateType, stringToHash } from "shape-edit/forms/helpers";
import { MOVE_CURSOR } from "template-edit/containers/image-preview";
import { RangeType } from "shape-edit/types";
import TextCursorSvg from "shape-entities/components/text-cursor-svg";
import { EditModeType } from "app/types/app-types";
import store from "store";
import { AdaptiveFontSizeAndStyle, getAdaptiveFontSize, WordsByLines } from "template-edit/components/helper";
import { isJson } from "app/helpers/other";
import StrokeUnderlayerFilter, { getStrokeUnderlayerFilterId, strokeUnderlayerFilterUrl } from "svg-text-utils/StrokeUnderlayerFilter";
import { hasPangoMarkup, parsePangoMarkup, renderPangoMarkup } from "svg-text-utils/pango-markup";

const { min, max, abs, sin, cos, PI, round, atan2, sqrt, ceil } = Math

export type TextAnchorType = 'start' | 'end' | "middle"
export type TextDecorationType = 'underline' | 'line-through' | 'overline' | 'none'

interface Props {
    shape: Shape
    width: number
    height: number
    dominantBaseline: 'central' | 'hanging' | 'mathematical'
    offsetY: number
    offsetX: number
    filter: string
    editMode: EditModeType
    isActive: boolean
    setRealSize: Function
    cursorPosition: number
    textSelection: RangeType
    textRanges: RangeType[]
    setCursorPosition: Function
    updateShape: Function
    handleTextClick: Function
    handleTextMouseDown: Function
    scalarToPosition: Function
    mainText: RefObject<SVGTextElement>
    tspanList: RefObject<SVGTSpanElement>[]
    trimmedContent: string
}

interface State {
    adaptiveFontSizeAndStyle: AdaptiveFontSizeAndStyle
}

export interface WordLengthType {
    word: string
    width: number
}

class SVGTextWrapped extends Component<Props, State> {
    underlayerFilterId = getStrokeUnderlayerFilterId()

    constructor(props) {
        super(props)

        this.state = {
            adaptiveFontSizeAndStyle: null,
        }
    }

    checkAndUpdateFontSize = () => {
        this.setState({
            adaptiveFontSizeAndStyle: getAdaptiveFontSize(this.props.shape, this.props.width, this.props.height, this.props.setRealSize)
        })
    }

    componentDidMount(): void {
        this.checkAndUpdateFontSize()
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && (
            prevProps.width !== this.props.width ||
            prevProps.height !== this.props.height ||
            prevProps.shape?.width !== this.props.shape?.width ||
            prevProps.shape?.height !== this.props.shape?.height ||
            prevProps.shape?.x !== this.props.shape?.x ||
            prevProps.shape?.y !== this.props.shape?.y ||
            prevProps.shape?.stroke_width !== this.props.shape?.stroke_width ||
            prevProps.shape?.text_stroke_color !== this.props.shape?.text_stroke_color ||
            prevProps.shape?.text_stroke_width !== this.props.shape?.text_stroke_width ||
            prevProps.shape?.text_stroke_underlayer !== this.props.shape?.text_stroke_underlayer ||
            prevProps.shape?.letter_spacing !== this.props.shape?.letter_spacing ||
            prevProps.shape?.line_spacing !== this.props.shape?.line_spacing ||
            prevProps.shape?.letter_case !== this.props.shape?.letter_case ||
            prevProps.shape?.text_padding !== this.props.shape?.text_padding ||
            prevProps.shape?.text_fontsize !== this.props.shape?.text_fontsize ||
            prevProps.shape?.text_fontfamily !== this.props.shape?.text_fontfamily ||
            prevProps.shape?.text_overflow !== this.props.shape?.text_overflow ||
            prevProps.shape?.text_content !== this.props.shape?.text_content
        )) {
            this.checkAndUpdateFontSize()
        }
    }

    getTSpanRef = (line: number): React.RefObject<SVGTSpanElement> => {
        if (typeof this.props.tspanList[line] === 'undefined') {
            this.props.tspanList[line] = React.createRef()
        }
        return this.props.tspanList[line]
    }

    render(): JSX.Element {
        const { shape, dominantBaseline, offsetY, offsetX, filter, isActive, cursorPosition, textSelection, textRanges, editMode, trimmedContent } = this.props
        const { adaptiveFontSizeAndStyle } = this.state

        const lineSpacing = shape.line_spacing ? shape.line_spacing : 0
        const textPadding = shape.text_padding ? shape.text_padding : 0
        const textDecorationColor = shape.text_decoration_color || shape.text_color || '#000'
        const textDecorationLineThickness = shape.text_decoration_linethickness || ceil(shape.text_fontsize / 20)
        const horizontalAlign = shape.text_align || 0
        const verticalAlign = shape.text_valign || 0

        const textAnchor: TextAnchorType = shape.text_align === TEXT_HORIZONTAL_ALIGN_RIGHT ? 'end' : (shape.text_align === TEXT_HORIZONTAL_ALIGN_LEFT ? 'start' : 'middle')

        if (!adaptiveFontSizeAndStyle) {
            console.debug(`error: not adaptive fontsize: SVGTextWrapped`)
            return null
        }

        const ss = store.getState() as StateType
        const isLocked = ss.lockedLayers && ss.lockedLayers.includes(shape.index)

        const isDecoration = shape.text_decoration && shape.text_decoration !== TEXT_DECORATION_NONE
        const allTextHeight = adaptiveFontSizeAndStyle.wordsByLines.length * (adaptiveFontSizeAndStyle.lineHeight + lineSpacing) - lineSpacing  //the line spacing on the end is not added

        const shapeWidthMinusPadding = this.props.width - 2 * textPadding
        const shapeHeightMinusPadding = this.props.height - 2 * textPadding

        let addOffsetY = .5 * (adaptiveFontSizeAndStyle.lineHeight - allTextHeight) + .5 * verticalAlign * (shapeHeightMinusPadding - allTextHeight)

        const lines = typeof shape.lines === 'string' && isJson(shape.lines) ? JSON.parse(shape.lines) : shape.lines
        const isTextInside = shape.type === TEXT_INSIDE_SHAPE_TYPE && lines
        if (isTextInside) {
            addOffsetY = 0
        }

        const bgcolor = shape.gradient_id && shape.gradient ? GradientFillUrl(shape.gradient_id) : (shape.text_background_color || '#transparent')
        const fillOpacity = shape.gradient_id && shape.gradient && shape.gradient.opacity ? shape.gradient.opacity / 100 : 1
        const cornerRadius = shape.corner_radius ?? null
        const isMarkup = hasPangoMarkup(trimmedContent)
        const allowCursor = isActive && !NO_TEXT_CURSOR_TYPES.includes(shape.type) && !isLocked

        const debugMetrics = process.env.APP_ENV === 'local' && !!process.env.DEBUG_TEXT_METRICS   //only on localhost, mirrors TextRenderer.php's $debugMetrics
        const metrics = debugMetrics ? getFontMetrics(adaptiveFontSizeAndStyle.updatedStyle) : null
        const metricsHeight = metrics ? metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent : adaptiveFontSizeAndStyle.lineHeight
        const baselineOffset = metrics ? .5 * (metrics.fontBoundingBoxAscent - metrics.fontBoundingBoxDescent) : 0

        const isStrokeUnderlayer = !!shape.text_stroke_underlayer && !!shape.text_stroke_color && !!shape.text_stroke_width
        const topTextStyle: CSSProperties = isStrokeUnderlayer ? { ...adaptiveFontSizeAndStyle.updatedStyle, stroke: undefined, strokeWidth: 0 } : adaptiveFontSizeAndStyle.updatedStyle

        //text anchor position of the line (textInsideShape: inside its measured line)
        const getLinePosition = (index: number): { x: number, y: number } => {
            const lineType = isTextInside && typeof lines[index] !== 'undefined' ? lines[index] : null
            const maxLineWidth = lineType && lineType.x1 && lineType.x2 ? (lineType.x2 - lineType.x1) : shapeWidthMinusPadding
            const addX = -.5 * horizontalAlign * (shapeWidthMinusPadding - maxLineWidth)
            const addY = index * (adaptiveFontSizeAndStyle.lineHeight + lineSpacing)

            return {
                x: lineType ? lineType.x1 + .5 * (lineType.x2 - lineType.x1) + addX : 0,
                y: lineType ? lineType.y : addY,
            }
        }

        const renderLines = (interactive: boolean) => adaptiveFontSizeAndStyle.wordsByLines.map((line: WordsByLines, index: number) => {
            const { x, y } = getLinePosition(index)

            return <tspan
                key={`tspan-${index}`}
                ref={interactive && isActive ? this.getTSpanRef(index) : undefined}
                x={x}
                y={y}
                dx={shape.letter_spacing ? (textAnchor === 'middle' ? `${round(shape.letter_spacing/2)}px`: textAnchor === 'end' ? `${round(shape.letter_spacing)}px` : undefined): undefined} //letter spacing leaves space after last char
                onClick={interactive && allowCursor ? (e) => this.props.handleTextClick(e, index) : undefined}
                onMouseDown={interactive && allowCursor ? (e) => this.props.handleTextMouseDown(e, index) : undefined}
            >{isMarkup ? renderPangoMarkup(line.words.join(' '), `tspan-${index}`, interactive) : line.words.join(' ')}</tspan>
        })

        return (
            <React.Fragment>
                <g transform={`translate(${offsetX}, ${offsetY + addOffsetY})`}>
                    <rect
                        className='move-box'
                        x={-.5 * this.props.width}
                        y={- .5 * this.props.height}
                        height={this.props.height}
                        width={this.props.width}
                        fill={'transparent'}
                        style={{
                            cursor: MOVE_CURSOR,
                        }}
                    />

                    {isTextInside && isActive &&
                        lines.map((lineType, i) => {
                            const addX = -.5 * horizontalAlign * shapeWidthMinusPadding
                            const addY = 0  //.5 * lineHeight
                            return <React.Fragment key={`ll_${i}_${shape.index}`}>
                                <line
                                    x1={lineType.x1 + addX}
                                    x2={lineType.x2 + addX}
                                    y1={lineType.y + addY}
                                    y2={lineType.y + addY}
                                    strokeWidth={textDecorationLineThickness}
                                    strokeDasharray={'5 1'}
                                    stroke={'#0000005a'}
                                />
                            </React.Fragment>
                        })
                    }

                    {shape.text_background_type === RECTANGLE_EACH_LINE_TYPE &&
                        <g>
                            {adaptiveFontSizeAndStyle.wordsByLines.map((line: WordsByLines, index: number) => {
                                const { x, y } = getLinePosition(index)
                                const x1 = x - .5 * (horizontalAlign + 1) * line.width
                                return <rect
                                    key={`background_shape_${shape.index}_${index}`}
                                    x={x1}
                                    y={y - .5 * adaptiveFontSizeAndStyle.lineHeight}
                                    height={adaptiveFontSizeAndStyle.lineHeight}
                                    width={line.width}
                                    fill={bgcolor}
                                    fillOpacity={fillOpacity}
                                    strokeWidth={shape.stroke_width}
                                    stroke={shape.stroke_color}
                                    filter={filter}
                                    rx={cornerRadius ? cornerRadius : undefined}
                                    ry={cornerRadius ? cornerRadius : undefined}
                                    strokeLinejoin={shape.line_join && LINE_JOIN_CONSTS[shape.line_join] ? LINE_JOIN_CONSTS[shape.line_join] : DEFAULT_LINE_JOIN}
                                    strokeLinecap={shape.line_cap && LINE_CAP_CONSTS[shape.line_cap] ? LINE_CAP_CONSTS[shape.line_cap] : DEFAULT_LINE_CAP}
                                />
                            })}
                        </g>
                    }

                    {debugMetrics && metrics &&
                        <g>
                            {adaptiveFontSizeAndStyle.wordsByLines.map((line: WordsByLines, index: number) => {
                                const { x, y } = getLinePosition(index)

                                const x1 = x - .5 * (horizontalAlign + 1) * line.width

                                return <React.Fragment key={`debug_metrics_${shape.index}_${index}`}>
                                    <rect
                                        x={x1}
                                        y={y - .5 * line.width * 0 - .5 * adaptiveFontSizeAndStyle.lineHeight}
                                        width={line.width}
                                        height={adaptiveFontSizeAndStyle.lineHeight}
                                        fill="#0000ff"
                                        fillOpacity={0.02}
                                        stroke="#000000"
                                        strokeWidth={1}
                                    />
                                    <rect
                                        x={x1 - 50}
                                        y={y - .5 * metricsHeight}
                                        width={line.width + 100}
                                        height={metricsHeight}
                                        fill="#ff0000"
                                        fillOpacity={0.055}
                                        stroke="#ffd901"
                                        strokeWidth={1}
                                    />
                                    <line
                                        x1={x1 - 50}
                                        x2={x1 + line.width + 50}
                                        y1={y - .5 * metricsHeight}
                                        y2={y - .5 * metricsHeight}
                                        stroke="#ff0000"
                                        strokeWidth={1}
                                    />
                                    <line
                                        x1={x1 - 50}
                                        x2={x1 + line.width + 50}
                                        y1={y + .5 * metricsHeight}
                                        y2={y + .5 * metricsHeight}
                                        stroke="#00ff00"
                                        strokeWidth={1}
                                    />
                                    <line
                                        x1={x1 - 50}
                                        x2={x1 + line.width + 50}
                                        y1={y + baselineOffset}
                                        y2={y + baselineOffset}
                                        stroke="#0000ff"
                                        strokeWidth={1}
                                    />
                                </React.Fragment>
                            })}
                        </g>
                    }

                    {isStrokeUnderlayer &&
                        <g filter={filter}>
                            <StrokeUnderlayerFilter
                                id={this.underlayerFilterId}
                                strokeWidth={shape.text_stroke_width}
                                box={{ x: 0, y: 0, width: this.props.width, height: this.props.height }}
                            />
                            <text
                                dominantBaseline={dominantBaseline}
                                textAnchor={textAnchor}
                                fill={shape.text_stroke_color}
                                style={{
                                    ...adaptiveFontSizeAndStyle.updatedStyle,
                                    pointerEvents: 'none'
                                }}
                                filter={strokeUnderlayerFilterUrl(this.underlayerFilterId, shape.text_stroke_width)}>
                                {renderLines(false)}
                            </text>
                        </g>
                    }

                    <text
                        ref={isActive ? this.props.mainText : undefined}
                        className={allowCursor ? `editable-text${isActive ? '-active' : ''}` : undefined}
                        dominantBaseline={dominantBaseline}
                        textAnchor={textAnchor}
                        fill={shape.text_color}
                        style={topTextStyle}
                        filter={isStrokeUnderlayer ? undefined : filter}>
                        {renderLines(true)}
                    </text>

                    {isDecoration ? adaptiveFontSizeAndStyle.wordsByLines.map((line: WordsByLines, index: number) => {
                        const decorationOffset = -.5 * adaptiveFontSizeAndStyle.lineHeight + (shape.text_decoration === TEXT_DECORATION_LINETHROUGH ? .5 * adaptiveFontSizeAndStyle.lineHeight : shape.text_decoration === TEXT_DECORATION_OVERLINE ? 0 : adaptiveFontSizeAndStyle.lineHeight)
                        const { x, y } = getLinePosition(index)
                        const x1 = x - .5 * (horizontalAlign + 1) * line.width

                        return <line
                            key={`decoration-line-${index}`}
                            x1={`${x1 - (shape.text_decoration_lineoverlap || 0)}`}
                            x2={`${x1 + line.width + (shape.text_decoration_lineoverlap || 0)}`}
                            y1={`${decorationOffset + y}`}
                            y2={`${decorationOffset + y}`}
                            strokeWidth={`${textDecorationLineThickness}`}
                            stroke={textDecorationColor}
                            strokeLinejoin={shape.line_join && LINE_JOIN_CONSTS[shape.line_join] ? LINE_JOIN_CONSTS[shape.line_join] : DEFAULT_LINE_JOIN}
                            strokeLinecap={shape.line_cap && LINE_CAP_CONSTS[shape.line_cap] ? LINE_CAP_CONSTS[shape.line_cap] : DEFAULT_LINE_CAP}
                        />
                    }) : null}

                    {allowCursor && editMode === 'text' &&
                        <TextCursorSvg
                            h={adaptiveFontSizeAndStyle.fontSize}
                            mainText={this.props.mainText}
                            tspanList={this.props.tspanList}
                            textContent={trimmedContent}
                            cursorPosition={cursorPosition}
                            textSelection={textSelection}
                            textRanges={textRanges}
                            setCursorPosition={this.props.setCursorPosition}
                            shape={shape}
                            updateShape={this.props.updateShape}
                            scalarToPosition={this.props.scalarToPosition}
                        />
                    }
                </g>
            </React.Fragment>
        )
    }
}

export const getStringWidthAndHeight = (str: string, style: CSSProperties): { w: number, h: number } => {
    const MEASUREMENT_ELEMENT_ID = '__react_svg_text_measurement_id';

    try {
        // Calculate length of each word to be used to determine number of words per line
        let textEl: any = document.getElementById(MEASUREMENT_ELEMENT_ID);
        let svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

        if (!textEl) {
            textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            textEl.setAttribute('id', MEASUREMENT_ELEMENT_ID);
            svg.appendChild(textEl);
            document.body.appendChild(svg);
        }

        Object.assign(textEl.style, style);
        textEl.textContent = '';
        if (hasPangoMarkup(str)) {
            //measure the styled runs, the markup tags themselves are not rendered
            parsePangoMarkup(str).forEach(run => {
                const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
                Object.assign(tspan.style, run.style);
                tspan.textContent = run.text;
                textEl.appendChild(tspan);
            })
        } else {
            textEl.textContent = str;
        }
        const box = textEl.getBBox()

        textEl.parentNode.parentNode.removeChild(textEl.parentNode);

        return { w: box.width, h: box.height }
    } catch (e) {
        return null;
    }
}

/**
 * Gets the font's own ascent+descent (the em-box the font was designed with), unlike getStringWidthAndHeight's
 * getBBox() which is a tight ink box around the actual glyphs of the given string - too small/inconsistent for
 * vertical alignment (e.g. it's 0 for empty/whitespace content, and doesn't reserve room for diacritics unless
 * the string already contains one).
 */
export const getFontMetrics = (style: CSSProperties): TextMetrics | null => {
    try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
            return null
        }

        ctx.font = `${style.fontWeight || ''} ${style.fontSize || ''} ${style.fontFamily || ''}`.trim()
        const metrics = ctx.measureText('')

        if (typeof metrics.fontBoundingBoxAscent !== 'number' || typeof metrics.fontBoundingBoxDescent !== 'number') {
            return null
        }



        return metrics
        //return metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent
    } catch (e) {
        return null
    }
}

export default SVGTextWrapped
